import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type PropsWithChildren,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../Icon';
import { Button } from './Button';
import { Modal } from './Modal';
import styles from './ActionDialog.module.scss';

export type ActionDialogTone = 'default' | 'warning' | 'danger';

export type ConfirmActionOptions = {
  readonly title?: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly tone?: ActionDialogTone;
  readonly icon?: IconName;
};

export type PromptActionOptions = ConfirmActionOptions & {
  readonly initialValue?: string;
  readonly inputLabel?: string;
  readonly placeholder?: string;
  readonly required?: boolean;
};

type ConfirmRequest = {
  readonly id: number;
  readonly kind: 'confirm';
  readonly options: ConfirmActionOptions;
  readonly resolve: (confirmed: boolean) => void;
};

type PromptRequest = {
  readonly id: number;
  readonly kind: 'prompt';
  readonly options: PromptActionOptions;
  readonly resolve: (value: string | null) => void;
};

type DialogRequest = ConfirmRequest | PromptRequest;

type ActionDialogContextValue = {
  readonly confirmAction: (options: ConfirmActionOptions) => Promise<boolean>;
  readonly promptForText: (options: PromptActionOptions) => Promise<string | null>;
};

const ActionDialogContext = createContext<ActionDialogContextValue | null>(null);

function requestIcon(request: DialogRequest): IconName {
  if (request.options.icon) return request.options.icon;
  if (request.options.tone === 'danger') return 'delete';
  return 'info';
}

export function ActionDialogProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const [request, setRequest] = useState<DialogRequest | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [showRequiredError, setShowRequiredError] = useState(false);
  const requestRef = useRef<DialogRequest | null>(null);
  const queueRef = useRef<DialogRequest[]>([]);
  const requestIdRef = useRef(0);

  const enqueue = useCallback((nextRequest: DialogRequest) => {
    if (requestRef.current) {
      queueRef.current.push(nextRequest);
      return;
    }
    requestRef.current = nextRequest;
    setRequest(nextRequest);
  }, []);

  const confirmAction = useCallback(
    (options: ConfirmActionOptions) =>
      new Promise<boolean>((resolve) => enqueue({
        id: ++requestIdRef.current,
        kind: 'confirm',
        options,
        resolve,
      })),
    [enqueue],
  );

  const promptForText = useCallback(
    (options: PromptActionOptions) =>
      new Promise<string | null>((resolve) => enqueue({
        id: ++requestIdRef.current,
        kind: 'prompt',
        options,
        resolve,
      })),
    [enqueue],
  );

  const settle = useCallback((value: boolean | string | null) => {
    const current = requestRef.current;
    if (!current) return;

    if (current.kind === 'confirm') {
      current.resolve(value === true);
    } else {
      current.resolve(typeof value === 'string' ? value : null);
    }

    const nextRequest = queueRef.current.shift() ?? null;
    requestRef.current = nextRequest;
    setRequest(nextRequest);
  }, []);

  useEffect(() => {
    if (request?.kind === 'prompt') {
      setInputValue(request.options.initialValue ?? '');
    } else {
      setInputValue('');
    }
    setShowRequiredError(false);
  }, [request]);

  useEffect(
    () => () => {
      const pending = [requestRef.current, ...queueRef.current].filter(
        (item): item is DialogRequest => item !== null,
      );
      pending.forEach((item) => {
        if (item.kind === 'confirm') {
          item.resolve(false);
        } else {
          item.resolve(null);
        }
      });
    },
    [],
  );

  function handleClose() {
    settle(request?.kind === 'confirm' ? false : null);
  }

  function handleConfirm(event?: FormEvent) {
    event?.preventDefault();
    if (!request) return;
    if (request.kind === 'confirm') {
      settle(true);
      return;
    }

    const value = inputValue.trim();
    if (request.options.required && !value) {
      setShowRequiredError(true);
      return;
    }
    settle(value);
  }

  const contextValue = useMemo(
    () => ({ confirmAction, promptForText }),
    [confirmAction, promptForText],
  );
  const tone = request?.options.tone ?? 'default';
  const title = request?.options.title ?? t('dialog.confirmTitle');
  const confirmLabel = request?.options.confirmLabel ?? t('dialog.confirm');
  const cancelLabel = request?.options.cancelLabel ?? t('dialog.cancel');

  return (
    <ActionDialogContext.Provider value={contextValue}>
      {children}
      {request ? (
        <Modal
          key={request.id}
          open
          onClose={handleClose}
          role="alertdialog"
          size="sm"
          compactMobile
          closeOnBackdrop
          closeAriaLabel={t('dialog.close')}
          title={(
            <span className={styles.titleRow}>
              <span className={[styles.icon, styles[tone]].join(' ')}>
                <Icon name={requestIcon(request)} size={20} />
              </span>
              <span>{title}</span>
            </span>
          )}
          subtitle={request.options.message}
          footer={(
            <>
              <Button
                variant="secondary"
                onClick={handleClose}
                data-autofocus={request.kind === 'confirm' ? true : undefined}
              >
                {cancelLabel}
              </Button>
              <Button
                variant={tone === 'danger' ? 'dangerSolid' : 'primary'}
                onClick={() => handleConfirm()}
              >
                {confirmLabel}
              </Button>
            </>
          )}
        >
          {request.kind === 'prompt' ? (
            <form className={styles.form} onSubmit={handleConfirm}>
              <label className={styles.label} htmlFor="action-dialog-input">
                {request.options.inputLabel ?? t('dialog.inputLabel')}
              </label>
              <textarea
                id="action-dialog-input"
                data-autofocus
                className={[styles.textarea, showRequiredError ? styles.invalid : ''].filter(Boolean).join(' ')}
                value={inputValue}
                placeholder={request.options.placeholder}
                rows={4}
                onChange={(event) => {
                  setInputValue(event.target.value);
                  if (event.target.value.trim()) setShowRequiredError(false);
                }}
                aria-invalid={showRequiredError}
                aria-describedby={showRequiredError ? 'action-dialog-error' : undefined}
              />
              {showRequiredError ? (
                <span id="action-dialog-error" className={styles.error} role="alert">
                  {t('dialog.required')}
                </span>
              ) : null}
              <button className={styles.hiddenSubmit} type="submit" tabIndex={-1} aria-hidden="true" />
            </form>
          ) : (
            <div className={styles.confirmSpacer} />
          )}
        </Modal>
      ) : null}
    </ActionDialogContext.Provider>
  );
}

export function useActionDialog(): ActionDialogContextValue {
  const context = useContext(ActionDialogContext);
  if (!context) {
    throw new Error('useActionDialog must be used within ActionDialogProvider');
  }
  return context;
}
